import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';

import FieldDateInput from '../FieldDateInput';

const StyledWrapper = styled.div`
  height: 100%;
  width: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  ${(props) => (typeof props.margin === 'number' ? `padding: ${props.margin}px;` : '')}
`;

/**
 * Why didn't I use a functional component here?
 *
 * The unmount functionality with the useEffect doesn't allow me
 * to access the focused and changes state that I needed. Inside
 * of the useEffect cleanup it always showed both these values
 * as undefined.
 *
 */
class TableDateField extends React.PureComponent {

  static propTypes = {

    fieldId: PropTypes.string,
    fieldIdentifier: PropTypes.string,
    fieldPositionId: PropTypes.string,
    fieldMode: PropTypes.string,
    fieldPresentationMode: PropTypes.string,
    pageLayoutMode: PropTypes.string,

    value: PropTypes.array,
    cellValue: PropTypes.string,
    rowId: PropTypes.string,

    disabled: PropTypes.bool,
    tabIndex: PropTypes.number,

    //SDK Styles
    primaryColor: PropTypes.string,

    //Field style
    fontSize: PropTypes.number,
    fontColor: PropTypes.string,
    fontStyle: PropTypes.string,
    fontWeight: PropTypes.string,
    textAlign: PropTypes.string,
    textTransform: PropTypes.string,
    textDecoration: PropTypes.string,
    textOverflow: PropTypes.string,
    borderColor: PropTypes.string,
    borderRadius: PropTypes.number,
    borderWidth: PropTypes.number,
    backgroundColor: PropTypes.string,
    padding: PropTypes.number,
    margin: PropTypes.number,

    //Action handlers
    onChange: PropTypes.func,
    onFocus: PropTypes.func,
    onBlur: PropTypes.func,

  }

  static defaultProps = {
    value: ''
  }

  constructor(props) {
    super(props);
    const { cellValue } = props;
    this.state = { focused: false, changes: cellValue };
  }

  componentDidUpdate(prevProps) {
    const { changes } = this.state;
    const { cellValue } = this.props;

    if (cellValue !== prevProps.cellValue && cellValue !== changes) this.setState({ changes: cellValue });
  }

  componentWillUnmount() {

    const { cellValue, disabled, fieldId, fieldIdentifier, fieldPositionId, rowId, columnIdentifier, column, onBlur } = this.props;
    const { changes, focused } = this.state;

    /**
     * This ensures that changes are saved when this component unmounts
     * without first losing focus. The return func in useEffect is the
     * same as firing cleanup stuff in comoponentWillUnmount.
     */
    if (focused && changes !== cellValue && !disabled) {

      this.handleCellChange(changes);

      onBlur(fieldId, fieldIdentifier, fieldPositionId, {}, rowId, column, columnIdentifier);
    }

  }

  render() {

    const { focused, changes } = this.state;

    const {
      fieldId,
      fieldIdentifier,
      fieldPositionId,

      rowId,
      column,
      columnIdentifier,

      cellValue,
      rowIndex,
      disabled,
      tabIndex,

      //Field style
      primaryColor,
      fontSize,
      fontColor,
      fontStyle,
      fontWeight,
      textAlign,
      textTransform,
      textDecoration,
      textOverflow,
      borderColor,
      borderRadius,
      borderWidth,
      backgroundColor,
      padding,
      margin,

      //Action handlers
      onFocus,
      onBlur,

      fieldMode,
      fieldPresentationMode,
      pageLayoutMode,
      tableColumns,
      fieldPositionTableColumns,
      value

    } = this.props;

    const targetColumnFormat = fieldPositionTableColumns && fieldPositionTableColumns[column] ? fieldPositionTableColumns[column].format : undefined;

    const targetRow =  value && value.length > 0 ? value[rowIndex] : {};
    const targetRowTimezone = targetRow? targetRow.tz : null

    return (
      <StyledWrapper {...{ margin }}>
        <FieldDateInput
          {...{
            //Config
            value: cellValue,
            disabled,
            timezone: targetRowTimezone,
            enableTimezoneAnchor: true,
            format: targetColumnFormat,

            //Action handlers
            onChange: this.handleChange,
            onFocus: this.handleFocus,
            onBlur: this.handleBlur,

            //Styles
            primaryColor,
            fontSize,
            fontColor,
            fontStyle,
            fontWeight,
            textAlign,
            textTransform,
            textDecoration,
            textOverflow,
            borderColor,
            borderRadius,
            borderWidth,
            backgroundColor,
            padding,
          }}
        />
      </StyledWrapper>
    );
  }

  handleFocus = (e) => {

    const { focused, disabled, onFocus, fieldId, fieldIdentifier, fieldPositionId, rowId, column, columnIdentifier } = this.props;

    if (!focused) this.setState({ focused: true });

    if (!disabled && onFocus) {
      onFocus(fieldId, fieldIdentifier, fieldPositionId, { blur: e.blur }, rowId, column, columnIdentifier);
    }

  }

  handleChange = (cellValue, nextTimezone) => {

    const { onChange, rowId, column, value, fieldId, fieldIdentifier, fieldPositionId } = this.props;

    const nextValue = [...value];

    const targetRowIndex = value.findIndex((row) => row._id === rowId);
    const nextRow = { ...nextValue[targetRowIndex] };

    /**<----------NOTICE---------------->
     * onChange will only be triggered if the row and column associated with the table cell is undeleted
    */
    if (nextRow && nextRow._id) {

      nextRow.cells = { ...nextRow.cells, [column]: cellValue };
      nextRow.tz = nextTimezone;
      nextValue[targetRowIndex] = nextRow;

      if (onChange) onChange(fieldId, fieldIdentifier, fieldPositionId, nextValue);

    }

  }

  handleBlur = () => {

    const { focused, onBlur, fieldId, fieldIdentifier, fieldPositionId, rowId, column, columnIdentifier } = this.props;

    if (focused) this.setState({ focused: false });

    if (onBlur) {
      onBlur(fieldId, fieldIdentifier, fieldPositionId, {}, rowId, column, columnIdentifier);
    }

  }

}

export default TableDateField;
